import { useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, InputLabel, Typography } from '@mui/material';

import { defaultLanguage } from 'context';
import { useCourseContext } from 'context';
import { ScaleRatingFormField } from 'types';

import { ScaleRatingField } from '../fields';

export const FormScaleRatingField = ({
  id,
  label,
  options,
  translations,
}: ScaleRatingFormField) => {
  const { language } = useCourseContext();
  const { t } = useTranslation(['course']);

  const translation = useMemo(() => {
    if (!language || !translations || language === defaultLanguage) {
      return {
        label,
        options: options?.options,
        minValueLabel: options?.minValueLabel || t('notSure'),
        maxValueLabel: options?.maxValueLabel || t('extremelyConfident'),
      };
    }

    const translation = translations[language];
    return {
      options: translation.options || options?.options,
      label: label ? translation?.label || label : label,
      minValueLabel:
        translation.minValueLabel || options?.minValueLabel || t('notSure'),
      maxValueLabel:
        translation.maxValueLabel ||
        options?.maxValueLabel ||
        t('extremelyConfident'),
    };
  }, [
    label,
    options?.options,
    options?.minValueLabel,
    options?.maxValueLabel,
    language,
    translations,
    t,
  ]);

  return (
    <Box className="flex flex-col gap-2">
      {translation.label && (
        <InputLabel className="mb-2" sx={{ whiteSpace: 'pre-line' }}>
          {translation.label}
        </InputLabel>
      )}
      {translation.options?.map((option, index) => (
        <Fragment key={index}>
          <ScaleRatingField
            label={option}
            name={`${id}.${index}`}
            min={options?.minValue ?? 1}
            max={options?.maxValue ?? 7}
            labelClassName={index === 0 ? 'md:pt-8' : undefined}
            header={
              index === 0 ? (
                <div className="flex flex-row items-center justify-between">
                  <Typography
                    variant="caption"
                    sx={{ color: 'gray.neutral', whiteSpace: 'pre-line' }}
                  >
                    {translation.minValueLabel}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: 'right',
                      color: 'gray.neutral',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {translation.maxValueLabel}
                  </Typography>
                </div>
              ) : undefined
            }
          />
          {index < translation.options!.length - 1 && (
            <Divider sx={{ my: 1.5 }} />
          )}
        </Fragment>
      ))}
    </Box>
  );
};
